import { css } from "@emotion/css";
import { RadioButtonGroup, RadioButton } from "@octopusdeploy/design-system-components";
import React from "react";
import ExternalLink from "~/components/Navigation/ExternalLink";
import { Note, Text } from "~/components/form";
import automaticDeploymentsInfoImage from "../assets/automatic-deployment-info-dark.svg";
import { ConfigWizardHelpLayout } from "./ConfigWizardHelpLayout";
import { ConfigWizardStatusBar } from "./ConfigWizardStatusBar";
import { NameTemplateExample, ShortNameTemplateInformation } from "./ConfigureNameTemplateContent";
export function AutoDeploymentHelp() {
    return (<ConfigWizardHelpLayout title="Auto Deployment">
            <p>
                Auto Deployment automatically creates an ephemeral environment from your name template when a release is created, runs the provisioning runbook, and deploys that release immediately. It removes the manual step of creating and wiring
                up an environment, giving you a ready-to-test space per change/PR. If you are not using auto deployment, nothing happens on release creation—you will need to provision and deploy later via the Portal, API/CLI, or your CI pipeline.
            </p>
            <ExternalLink href={"ephemeralenvironments"}>Documentation</ExternalLink>.
        </ConfigWizardHelpLayout>);
}
export const automaticDeploymentHelpImage = {
    src: automaticDeploymentsInfoImage,
    altText: "Graphics showing a release being created and it either being automatically or manually deployed. The automatic deployment shows a deployment being created, an environment being automatically created from the branch name, the provisioning runbook running, and the release being automatically deployed to the environment.",
};
const styles = {
    parentRadioGroup: css({
        width: "30rem",
    }),
    nestedRadioGroup: css({
        width: "25rem",
    }),
    body: css({
        height: "30rem",
    }),
};
interface AutomaticDeploymentsContentProps {
    isGitProject: boolean;
    nameTemplateConfig: string;
    setNameTemplateConfig: (config: string) => void;
    useAutomaticBranchName?: boolean;
    setUseAutomaticBranchName: (value: boolean) => void;
    automaticEphemeralEnvironmentDeployments: boolean;
    setAutomaticEphemeralEnvironmentDeployments: (value: boolean) => void;
    isCommittingRunbooksToNewBranch: boolean;
}
interface GitProjectRadioButtonsProps {
    nameTemplateConfig: string;
    setNameTemplateConfig: (config: string) => void;
    useAutomaticBranchName?: boolean;
    setUseAutomaticBranchName: (value: boolean) => void;
    automaticEphemeralEnvironmentDeployments: boolean;
    setAutomaticEphemeralEnvironmentDeployments: (value: boolean) => void;
    defaultBranchNameTemplate: string;
}
function GitProjectRadioButtons({ nameTemplateConfig, setNameTemplateConfig, automaticEphemeralEnvironmentDeployments, setAutomaticEphemeralEnvironmentDeployments, useAutomaticBranchName, setUseAutomaticBranchName, defaultBranchNameTemplate, }: GitProjectRadioButtonsProps) {
    return (<RadioButtonGroup className={styles.parentRadioGroup} value={automaticEphemeralEnvironmentDeployments ? "true" : "false"} onChange={(value) => {
            setAutomaticEphemeralEnvironmentDeployments(value === "true");
            if (value === "true") {
                if (useAutomaticBranchName)
                    setNameTemplateConfig(defaultBranchNameTemplate);
                else
                    setNameTemplateConfig("");
            }
            else {
                setNameTemplateConfig("");
            }
        }}>
            <RadioButton value={"true"} label={"Automatically deploy to ephemeral environments"}/>
            <Note>Ephemeral environments will be created and deployed automatically from releases</Note>
            {automaticEphemeralEnvironmentDeployments && (<>
                    <RadioButtonGroup className={styles.nestedRadioGroup} value={useAutomaticBranchName ? "true" : "false"} onChange={(value) => {
                setUseAutomaticBranchName(value === "true");
                setNameTemplateConfig(value === "true" ? defaultBranchNameTemplate : "");
            }}>
                        <RadioButton value={"true"} label={"Use branch name"} isDefault={true}/>
                        <RadioButton value={"false"} label={"Configure my own"}/>
                        {useAutomaticBranchName === false ? (<Text label="Environment name template" value={nameTemplateConfig} onChange={(value) => setNameTemplateConfig(value)} helperText={<NameTemplateExample isGitProject={true} setNameTemplateConfig={setNameTemplateConfig}/>}/>) : (<></>)}
                    </RadioButtonGroup>
                    <ShortNameTemplateInformation />
                </>)}
            <RadioButton value={"false"} label="Don't automatically deploy to ephemeral environments"/>
            <Note>Ephemeral environments need to be created and releases deployed manually</Note>
        </RadioButtonGroup>);
}
interface DbProjectRadioButtonsProps {
    nameTemplateConfig: string;
    setNameTemplateConfig: (config: string) => void;
    automaticEphemeralEnvironmentDeployments: boolean;
    setAutomaticEphemeralEnvironmentDeployments: (value: boolean) => void;
}
function DbProjectRadioButtons({ nameTemplateConfig, setNameTemplateConfig, automaticEphemeralEnvironmentDeployments, setAutomaticEphemeralEnvironmentDeployments }: DbProjectRadioButtonsProps) {
    return (<RadioButtonGroup className={styles.parentRadioGroup} value={automaticEphemeralEnvironmentDeployments ? "true" : "false"} onChange={(value) => {
            setAutomaticEphemeralEnvironmentDeployments(value === "true");
            setNameTemplateConfig("");
        }}>
            <RadioButton value={"true"} label={"Automatically deploy to ephemeral environments"}/>
            <Note>Ephemeral environments will be created and deployed automatically from releases</Note>

            {automaticEphemeralEnvironmentDeployments && (<>
                    <Text label="Environment name template" value={nameTemplateConfig} onChange={(value) => setNameTemplateConfig(value)} helperText={<NameTemplateExample isGitProject={false} setNameTemplateConfig={setNameTemplateConfig}/>}/>
                    <ShortNameTemplateInformation />
                </>)}
            <RadioButton value={"false"} label="Don't automatically deploy to ephemeral environments"/>
            <Note>Ephemeral environments need to be created and releases deployed manually</Note>
        </RadioButtonGroup>);
}
export const defaultBranchNameTemplate = "#{Octopus.Release.Git.BranchName}";
export function AutomaticDeploymentsContent({ isGitProject, nameTemplateConfig, setNameTemplateConfig, isCommittingRunbooksToNewBranch, automaticEphemeralEnvironmentDeployments, setAutomaticEphemeralEnvironmentDeployments, useAutomaticBranchName, setUseAutomaticBranchName, }: AutomaticDeploymentsContentProps) {
    return (<>
            <div className={styles.body}>
                <ConfigWizardStatusBar activeStage="Auto Deploy" isCommittingRunbooksToNewBranch={isCommittingRunbooksToNewBranch}/>
                <h3>Auto Deployment</h3>
                {isGitProject ? (<GitProjectRadioButtons nameTemplateConfig={nameTemplateConfig} setNameTemplateConfig={setNameTemplateConfig} automaticEphemeralEnvironmentDeployments={automaticEphemeralEnvironmentDeployments} setAutomaticEphemeralEnvironmentDeployments={setAutomaticEphemeralEnvironmentDeployments} useAutomaticBranchName={useAutomaticBranchName} setUseAutomaticBranchName={setUseAutomaticBranchName} defaultBranchNameTemplate={defaultBranchNameTemplate}/>) : (<DbProjectRadioButtons nameTemplateConfig={nameTemplateConfig} setNameTemplateConfig={setNameTemplateConfig} automaticEphemeralEnvironmentDeployments={automaticEphemeralEnvironmentDeployments} setAutomaticEphemeralEnvironmentDeployments={setAutomaticEphemeralEnvironmentDeployments}/>)}
                <ExternalLink href={"ephemeralenvironments"}>Learn about automatic deployments</ExternalLink>.
            </div>
        </>);
}
